import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactJson from '@uiw/react-json-view';
import { useNavigate } from 'react-router';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { defer } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Input } from '/imports/ui/Input';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { getEgaugeRegisters, modelClass } from '/imports/api/egaugeMeters';
import { toastOnError } from '/imports/ui/utils';

const EgaugeMeterPageStyled = styled(Page)`
`;

/** Page to register an eGauge production meter by fetching and identifying its registers. */
export const EgaugeMeterPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const navigate = useNavigate();
  const [initializing, setInitializing] = useState(true);
  const [domain, setDomain] = useState<string>();
  const [username, setUsername] = useState<string | undefined>('owner');
  const [password, setPassword] = useState<string>();
  const [idx, setIdx] = useState<string>();
  const [registers, setRegisters] = useState<any>();
  const [error, setError] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => setInitializing(false), []);
  useEffect(() => { !initializing && defer(() => setInitialized(true)); }, [initializing]);
  useEffect(() => {
    if (!domain || !username || !password) return;
    (async () => {
      const registers = await getEgaugeRegisters.call({ domain, username, password });
      setRegisters(registers);
    })();
  }, [domain, username, password]);
  /** Creates the eGauge meter and navigates to its production-meter page. */
  const save = toastOnError(async () => {
    if (!domain || !username || !password || !idx) return;
    setSubmitting(true);
    const egaugeId = await modelClass.createMethod.call({ domain, username, password, idx });
    const egaugeMeter = await modelClass.getMethod.call(egaugeId);
    setError(undefined);
    toast.success(strings.Saved());
    navigate(`/production-meters/${egaugeMeter.meterId}`);
  }, '', () => setSubmitting(false));
  if (initializing) return <LoadingFlex />;
  return (
    <EgaugeMeterPageStyled className={`page${!initialized ? ' initializing' : ''}`}
      disabled={submitting}
    >
      <PageHeading
        left={<h1>Egauge</h1>}
        right={<Button onClick={() => save()} disabled={!domain || !username || !password || !idx}>{strings.Save()}</Button>}
      />
      <PageBody>
        <Form onSubmit={() => save()}>
          <div className="field-group">
            <FormField field="domain" error={error}>
              <label>{strings.Domain()}</label>
              <Input value={domain} onChange={(domain) => setDomain(domain)} />
            </FormField>
            <FormField field="username" error={error}>
              <label>{strings.Username()}</label>
              <Input value={username} onChange={(username) => setUsername(username)} />
            </FormField>
            <FormField field="password" error={error}>
              <label>{strings.Password()}</label>
              <Input value={password} type="password" onChange={(password) => setPassword(password)} />
            </FormField>
            <FormField field="idx" error={error}>
              <label>idx</label>
              <Input value={idx} onChange={(idx) => setIdx(idx)} />
            </FormField>
          </div>
          <div>{registers && <>Identify the &apos;idx&apos; of the register of type &apos;P&apos; (Power) for this meter</>}</div>
          <div>{registers && <ReactJson value={registers} />}</div>
        </Form>
      </PageBody>
    </EgaugeMeterPageStyled>
  );
};
